import React, { FC, useMemo, useState } from 'react';
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridRowId,
    GridRowModes,
    GridRowModesModel,
    useGridApiRef,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Alert, CircularProgress, IconButton } from '@mui/material';
import useMerchantTranslation from '@hooks/useMerchantTranslation';
import styles from './AnalyticsTagEditorComponent.module.scss';
import SnackbarUtils from '../../snackbar-utils';
import { FormValidators, Validators } from '../../helpers/validators';

export const MAX_TAGS = 10;
export const MAX_KEY_LENGTH = 50;
export const MAX_VALUE_LENGTH = 50;

interface TagRow {
    id: GridRowId;
    key: string;
    value: string;
}

interface TagRowContext {
    otherKeys: string[];
}

interface AnalyticsTagEditorComponentProps {
    tags: Record<string, string>;
    onSave: (tags: Record<string, string>) => Promise<unknown>;
    disabled?: boolean;
}

class TagValidationError extends Error {}

const AnalyticsTagEditorComponent: FC<AnalyticsTagEditorComponentProps> = ({ tags, onSave, disabled = false }) => {
    const { t } = useMerchantTranslation(['common']);
    const apiRef = useGridApiRef();
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [isSaving, setIsSaving] = useState(false);
    const [savingRowId, setSavingRowId] = useState<GridRowId | null>(null);
    const isLocked = disabled || isSaving;
    const isEditing = Object.values(rowModesModel).some((m) => m.mode === GridRowModes.Edit);

    const rows = useMemo<TagRow[]>(
        () => Object.entries(tags ?? {}).map(([key, value], index) => ({ id: index + 1, key, value })),
        [tags],
    );

    const toDictionary = (updated: TagRow[]) => updated.reduce<Record<string, string>>((acc, row) => {
        acc[row.key.trim().toLowerCase()] = row.value.trim().toLowerCase();
        return acc;
    }, {});

    const invalidKey = t('common:analytics_tags.errors.invalid_key', { max: MAX_KEY_LENGTH });
    const invalidValue = t('common:analytics_tags.errors.invalid_value', { max: MAX_VALUE_LENGTH });
    const validators: FormValidators = {
        key: [
            (v) => Validators.required(v?.trim(), invalidKey),
            (v) => Validators.regex(v?.trim(), `^[A-Za-z][A-Za-z0-9_]{0,${MAX_KEY_LENGTH - 1}}$`, invalidKey),
            (v, ctx: TagRowContext) => Validators.notContainedIn(
                v?.trim().toLowerCase(),
                ctx.otherKeys,
                t('common:analytics_tags.errors.duplicate_key'),
            ),
        ],
        value: [
            (v) => Validators.required(v?.trim(), invalidValue),
            (v) => Validators.regex(v?.trim(), `^[\\s\\S]{1,${MAX_VALUE_LENGTH}}$`, invalidValue),
        ],
    };

    const contextFor = (id: GridRowId): TagRowContext => ({
        otherKeys: rows.filter((r) => r.id !== id).map((r) => r.key.trim().toLowerCase()),
    });

    const fieldError = (field: 'key' | 'value', value: string, id: GridRowId) => (
        Validators.validate(validators, field, value ?? '', contextFor(id))?.errorSummary ?? null
    );

    const rowError = (row: TagRow) => fieldError('key', row.key, row.id) ?? fieldError('value', row.value, row.id);

    const commit = async (updated: TagRow[], rowId: GridRowId | null = null) => {
        setIsSaving(true);
        setSavingRowId(rowId);
        try {
            await onSave(toDictionary(updated));
        } finally {
            setIsSaving(false);
            setSavingRowId(null);
        }
    };

    const addTag = () => {
        if (rows.length >= MAX_TAGS) {
            SnackbarUtils.error(t('common:analytics_tags.errors.too_many', { max: MAX_TAGS }));
            return;
        }
        apiRef.current.updateRows([{ id: 0, key: '', value: '' }]);
        setRowModesModel({ ...rowModesModel, 0: { mode: GridRowModes.Edit, fieldToFocus: 'key' } });
    };

    const handleEditClick = (id: GridRowId) => () => {
        if (!isEditing && !isLocked) {
            setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'key' } });
        }
    };

    // processRowUpdate validates; when it throws, the grid reverts the row to edit mode via onRowModesModelChange
    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });
        apiRef.current.updateRows([{ id: 0, _action: 'delete' }]);
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        commit(rows.filter((r) => r.id !== id)).catch(() => {});
    };

    const processRowUpdate = async (updatedRow: TagRow) => {
        const error = rowError(updatedRow);
        if (error) {
            throw new TagValidationError(error);
        }

        await commit([...rows.filter((r) => r.id !== updatedRow.id), updatedRow], updatedRow.id);

        if (updatedRow.id === 0) {
            apiRef.current.updateRows([{ id: 0, _action: 'delete' }]);
        }
        return updatedRow;
    };

    const columns: GridColDef[] = [
        {
            field: 'key',
            headerName: t('common:analytics_tags.key'),
            flex: 1,
            editable: true,
        },
        {
            field: 'value',
            headerName: t('common:analytics_tags.value'),
            flex: 1,
            editable: true,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('common:analytics_tags.add'),
            renderHeader: () => (
                <IconButton
                    aria-label={t('common:analytics_tags.add')}
                    disabled={isEditing || isLocked}
                    onClick={() => addTag()}
                >
                    <AddIcon />
                </IconButton>
            ),
            getActions: ({ id }) => {
                const isRowSaving = savingRowId === id;
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit || isRowSaving;

                return isInEditMode ? [
                    <GridActionsCellItem
                        icon={isRowSaving ? <CircularProgress color="info" size={16} /> : <SaveIcon />}
                        label="Save"
                        disabled={isLocked}
                        sx={{ color: 'primary.main' }}
                        onClick={handleSaveClick(id)}
                    />,
                    <GridActionsCellItem
                        icon={<CancelIcon />}
                        label="Cancel"
                        className="textPrimary"
                        color="inherit"
                        disabled={isLocked}
                        onClick={handleCancelClick(id)}
                    />,
                ] : [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        color="inherit"
                        disabled={isLocked || isEditing}
                        onClick={handleEditClick(id)}
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary"
                        color="inherit"
                        disabled={isLocked || isEditing}
                        onClick={handleDeleteClick(id)}
                    />,
                ];
            },
        },
    ];

    return (
        <div className={styles.AnalyticsTagEditorComponent} data-testid="AnalyticsTagEditorComponent">
            <Alert severity="info" sx={{ mb: 2 }}>{t('common:analytics_tags.data_lake_notice')}</Alert>
            <DataGrid
                apiRef={apiRef}
                editMode="row"
                density="compact"
                rowModesModel={rowModesModel}
                rows={rows}
                columns={columns}
                onRowModesModelChange={setRowModesModel}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(error: Error) => {
                    if (error instanceof TagValidationError) {
                        SnackbarUtils.error(error.message);
                    }
                }}
                hideFooter
            />
        </div>
    );
};

AnalyticsTagEditorComponent.defaultProps = {
    disabled: false,
};

export default AnalyticsTagEditorComponent;
